import { useState, useEffect } from 'react'
import { useAdmin } from '../context/AdminContext'
import { createAdminApi, AuditSessionDetail, AuditRequest, AuditMsg } from '../services/adminApi'
import styles from './AuditSessionDetailPage.module.css'

interface Props {
  sessionId: string
}

function msgClass(role: string) {
  if (role === 'user') return styles.userMsg
  if (role === 'assistant') return styles.assistantMsg
  return styles.systemMsg
}

function MessageRow({ msg }: { msg: AuditMsg }) {
  const [expanded, setExpanded] = useState(msg.role !== 'system')

  return (
    <div className={`${styles.message} ${msgClass(msg.role)}`}>
      <div className={styles.msgHeader} onClick={() => setExpanded(e => !e)}>
        <span className={styles.msgRole}>{msg.role}</span>
        <span className={`${styles.msgChevron} ${expanded ? styles.msgChevronOpen : ''}`}>▶</span>
      </div>
      {expanded && <div className={styles.msgContent}>{msg.content}</div>}
    </div>
  )
}

function RequestCard({ req }: { req: AuditRequest }) {
  const [open, setOpen] = useState(false)

  const ts = new Date(req.createdAt).toLocaleString()

  return (
    <div className={styles.requestCard}>
      <div className={styles.requestHeader} onClick={() => setOpen(o => !o)}>
        <span className={styles.requestTime}>{ts}</span>
        <span className={styles.typeBadge}>{req.llmType}</span>
        <span className={styles.modelName}>{req.model}</span>
        <span className={styles.msgCount}>{req.messages.length} msgs</span>
        <span className={`${styles.chevron} ${open ? styles.chevronOpen : ''}`}>▶</span>
      </div>

      {open && (
        <div className={styles.requestBody}>
          {req.messages.length > 0 && (
            <div className={styles.messagesSection}>
              <h3>Prompt Messages</h3>
              <div className={styles.messages}>
                {req.messages.map((m, i) => (
                  <MessageRow key={i} msg={m} />
                ))}
              </div>
            </div>
          )}

          <div className={styles.responseSection}>
            <h3>Response</h3>
            <pre className={styles.responseText}>{req.response}</pre>
          </div>
        </div>
      )}
    </div>
  )
}

export default function AuditSessionDetailPage({ sessionId }: Props) {
  const { apiKey } = useAdmin()

  const [detail, setDetail] = useState<AuditSessionDetail | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')

  useEffect(() => {
    setLoading(true)
    setError('')
    const api = createAdminApi()
    api.getAuditSession(sessionId)
      .then(d => { setDetail(d); setLoading(false) })
      .catch(e => { setError(e instanceof Error ? e.message : String(e)); setLoading(false) })
  }, [sessionId, apiKey])

  if (loading) {
    return (
      <div className={styles.page}>
        <header className={styles.header}>
          <a href="#audit" className={styles.back}>← Audit</a>
        </header>
        <p className={styles.loading}>Loading…</p>
      </div>
    )
  }

  if (error) {
    return (
      <div className={styles.page}>
        <header className={styles.header}>
          <a href="#audit" className={styles.back}>← Audit</a>
        </header>
        <p className={styles.errorMsg}>{error}</p>
      </div>
    )
  }

  if (!detail) return null

  return (
    <div className={styles.page}>
      <header className={styles.header}>
        <a href="#audit" className={styles.back}>← Audit</a>
        <h1>Session <code>{detail.id.length > 16 ? `${detail.id.slice(0, 16)}…` : detail.id}</code></h1>
        <span className={styles.headerMeta}>
          {new Date(detail.createdAt).toLocaleString()} · {detail.requests.length} request{detail.requests.length !== 1 ? 's' : ''}
        </span>
      </header>

      <div className={styles.content}>
        {detail.requests.length === 0 ? (
          <p className={styles.empty}>No LLM requests recorded for this session.</p>
        ) : (
          detail.requests.map(req => <RequestCard key={req.id} req={req} />)
        )}
      </div>
    </div>
  )
}
